import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import useProjectsApi from './useProjectsApi'

const withoutId = item => {
	if (!item || typeof item !== 'object') return item
	const { _id, id, ...rest } = item
	return rest
}

const cloneProjectSteps = steps => (
	(Array.isArray(steps) ? steps : []).map(step => ({
		...withoutId(step),
		certificates: (Array.isArray(step?.certificates) ? step.certificates : []).map(withoutId),
		pozycje: (Array.isArray(step?.pozycje) ? step.pozycje : []).map(pozycja => ({
			...withoutId(pozycja),
			certificates: (Array.isArray(pozycja?.certificates) ? pozycja.certificates : []).map(withoutId),
			inne_koszty: (Array.isArray(pozycja?.inne_koszty) ? pozycja.inne_koszty : []).map(withoutId),
		})),
	}))
)

export default function useProjectsData() {
	const { t } = useTranslation()
	const { createProject, deleteProject, getAllProjects, getProjectById, searchProjects, updateProject } = useProjectsApi()
	const defaultLimit = 25
	const limitIncrement = 25
	const defaultSortColumn = 'created'
	const defaultSortDirection = 'desc'

	const [data, setData] = useState([])
	const [loading, setLoading] = useState(true)
	const [loadingMore, setLoadingMore] = useState(false)
	const [error, setError] = useState(null)
	const [totalCount, setTotalCount] = useState(0)
	const [hasMore, setHasMore] = useState(false)
	const [limit, setLimit] = useState(defaultLimit)
	const [filters, setFilters] = useState({})
	const [sortColumn, setSortColumn] = useState(defaultSortColumn)
	const [sortDirection, setSortDirection] = useState(defaultSortDirection)
	const [isStartDateFilterOpen, setIsStartDateFilterOpen] = useState(false)
	const [isTrenerFilterOpen, setIsTrenerFilterOpen] = useState(false)
	const loadingMoreRef = useRef(false)

	const hasStartDateFilter = useMemo(
		() => !!(filters.fromDate || filters.toDate),
		[filters.fromDate, filters.toDate],
	)

	const hasTrenerFilter = useMemo(
		() => !!filters.trainerId,
		[filters.trainerId],
	)

	const loadData = useCallback(async () => {
		if (!loadingMoreRef.current) setLoading(true)
		setError(null)

		try {
			const searchText = `${filters.searchText || ''}`.trim()
			const requestOptions = {
				limit,
				sortColumn,
				sortDirection,
				filters: {
					status: filters.status || '',
					trainerId: filters.trainerId || '',
					placowkaId: filters.placowkaId || '',
					fromDate: filters.fromDate || '',
					toDate: filters.toDate || '',
				},
			}
			const result = searchText
				? await searchProjects(searchText, requestOptions)
				: await getAllProjects(requestOptions)

			if (result?.error) {
				setError(result.error)
				setData([])
				setTotalCount(0)
				setHasMore(false)
			} else {
				setData(Array.isArray(result?.data) ? result.data : [])
				setTotalCount(result?.totalCount || 0)
				setHasMore(!!result?.hasMore)
			}
		} catch (err) {
			console.error('useProjectsData.loadData error:', err)
			setError(err?.message || 'Failed to load projects')
			setData([])
			setTotalCount(0)
			setHasMore(false)
		} finally {
			loadingMoreRef.current = false
			setLoading(false)
			setLoadingMore(false)
		}
	}, [filters, getAllProjects, limit, searchProjects, sortColumn, sortDirection])

	const clearError = useCallback(() => setError(null), [])

	useEffect(() => {
		if (error) {
			Swal.fire({
				title: t('notifications.errorTitle'),
				text: error,
				icon: 'error',
			}).then(() => clearError())
		}
	}, [clearError, error, t])

	useEffect(() => {
		loadData()
	}, [loadData])

	const handleLoadMore = useCallback(() => {
		if (hasMore && !loadingMoreRef.current) {
			loadingMoreRef.current = true
			setLimit(prev => prev + limitIncrement)
			setLoadingMore(true)
		}
	}, [hasMore])

	const setSorting = useCallback((column, direction) => {
		setSortColumn(column)
		setSortDirection(direction)
	}, [])

	const updateFilters = useCallback(newFilters => {
		setFilters(prev => ({ ...prev, ...newFilters }))
	}, [])

	const handleSort = useCallback(column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setSorting(column, newDirection)
	}, [setSorting, sortColumn, sortDirection])

	const handleSearchChange = useCallback(event => {
		updateFilters({ searchText: event.target.value })
	}, [updateFilters])

	const handleApplyStartDateFilter = useCallback((fromDate, toDate) => {
		updateFilters({
			fromDate: fromDate || null,
			toDate: toDate || null,
		})
		setIsStartDateFilterOpen(false)
	}, [updateFilters])

	const handleApplyTrenerFilter = useCallback(trainerId => {
		updateFilters({ trainerId: trainerId || null })
		setIsTrenerFilterOpen(false)
	}, [updateFilters])

	const handleDelete = useCallback(async project => {
		const projectName = project?.nazwa || '—'
		const result = await Swal.fire({
			title: t('projects.list.deleteDialog.confirmTitle'),
			text: t('projects.list.deleteDialog.confirmText', { name: projectName }),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: t('projects.list.deleteDialog.confirmButton'),
			cancelButtonText: t('projects.list.deleteDialog.cancelButton'),
			confirmButtonColor: '#dc2626',
		})

		if (!result.isConfirmed) return

		const deleteResult = await deleteProject(project?._id)
		if (!deleteResult.success) {
			await Swal.fire({
				title: t('projects.list.deleteDialog.errorTitle'),
				text: deleteResult.error || t('projects.list.deleteDialog.errorFallback'),
				icon: 'error',
			})
			return
		}

		await loadData()
	}, [deleteProject, loadData, t])

	const handleDuplicate = useCallback(async project => {
		const sourceName = `${project?.nazwa || ''}`.trim()
		const prompt = await Swal.fire({
			title: t('projects.list.duplicateDialog.title', { defaultValue: 'Powiel projekt' }),
			text: t('projects.list.duplicateDialog.prompt', { defaultValue: 'Podaj nazwę nowego projektu.' }),
			input: 'text',
			inputValue: sourceName ? `${sourceName} (kopia)` : '',
			inputPlaceholder: t('projects.list.duplicateDialog.placeholder', { defaultValue: 'Nazwa projektu' }),
			showCancelButton: true,
			confirmButtonText: t('projects.list.duplicateDialog.confirmButton', { defaultValue: 'Powiel' }),
			cancelButtonText: t('projects.list.deleteDialog.cancelButton', { defaultValue: 'Anuluj' }),
			confirmButtonColor: '#4f46e5',
			inputValidator: value => {
				if (!`${value || ''}`.trim()) {
					return t('projects.list.duplicateDialog.nameRequired', { defaultValue: 'Podaj nazwę projektu' })
				}
				return undefined
			},
		})

		if (!prompt.isConfirmed) return

		const nazwa = `${prompt.value || ''}`.trim()
		const sourceResult = await getProjectById(project?._id)
		if (sourceResult?.error || !sourceResult?.data) {
			await Swal.fire({
				title: t('projects.list.duplicateDialog.errorTitle', { defaultValue: 'Błąd' }),
				text: sourceResult?.error || t('projects.list.duplicateDialog.errorFallback', { defaultValue: 'Nie udało się powielić projektu' }),
				icon: 'error',
			})
			return
		}

		const source = sourceResult.data
		const created = await createProject({
			nazwa,
			status: source.status || 'new',
		})
		if (!created.success || !created.data?._id) {
			await Swal.fire({
				title: t('projects.list.duplicateDialog.errorTitle', { defaultValue: 'Błąd' }),
				text: created.error || t('projects.list.duplicateDialog.errorFallback', { defaultValue: 'Nie udało się powielić projektu' }),
				icon: 'error',
			})
			return
		}

		const updated = await updateProject(created.data._id, {
			nazwa,
			status: source.status || 'new',
			opis: source.opis || '',
			uwagi: source.uwagi || '',
			steps: cloneProjectSteps(source.steps),
		})
		if (!updated.success) {
			await deleteProject(created.data._id)
			await Swal.fire({
				title: t('projects.list.duplicateDialog.errorTitle', { defaultValue: 'Błąd' }),
				text: updated.error || t('projects.list.duplicateDialog.errorFallback', { defaultValue: 'Nie udało się powielić projektu' }),
				icon: 'error',
			})
			return
		}

		await Swal.fire({
			title: t('projects.list.duplicateDialog.successTitle', { defaultValue: 'Powielono' }),
			text: t('projects.list.duplicateDialog.successText', {
				defaultValue: 'Utworzono projekt „{{name}}”.',
				name: nazwa,
			}),
			icon: 'success',
		})
		await loadData()
	}, [createProject, deleteProject, getProjectById, loadData, t, updateProject])

	return {
		data,
		totalCount,
		hasMore,
		loading,
		loadingMore,
		error,
		sortColumn,
		sortDirection,
		searchQuery: filters.searchText || '',
		fromDate: filters.fromDate || '',
		toDate: filters.toDate || '',
		selectedTrainerId: filters.trainerId || null,
		hasStartDateFilter,
		hasTrenerFilter,
		isStartDateFilterOpen,
		setIsStartDateFilterOpen,
		isTrenerFilterOpen,
		setIsTrenerFilterOpen,
		loadData,
		handleLoadMore,
		handleSort,
		handleSearchChange,
		handleApplyStartDateFilter,
		handleApplyTrenerFilter,
		handleDelete,
		handleDuplicate,
		setSorting,
		updateFilters,
		clearError,
	}
}
